import { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import dashboardService from "../services/dashboardService";
import blService from "../services/blService";
import Modal from "../components/common/Modal";
import { formatDateTime } from "../utils/dateUtils";
import { API_CONFIG } from "../utils/apiConfig";

const QUICK_ACCESS = [
  {
    title: "Add Purchase",
    path: "/purchase-master/new",
    icon: "shopping_cart",
    color: "bg-blue-500",
    shadow: "shadow-blue-200",
  },
  {
    title: "Add Lot",
    path: "/lot",
    icon: "layers",
    color: "bg-emerald-500",
    shadow: "shadow-emerald-200",
  },
  {
    title: "Add Yard",
    path: "/yard",
    icon: "warehouse",
    color: "bg-amber-500",
    shadow: "shadow-amber-200",
  },
  {
    title: "Add Vendor",
    path: "/vendors",
    icon: "store",
    color: "bg-purple-500",
    shadow: "shadow-purple-200",
  },
  {
    title: "Add Customer",
    path: "/customer-master",
    icon: "person_add",
    color: "bg-rose-500",
    shadow: "shadow-rose-200",
  },
  {
    title: "Bill Report",
    path: "/reports/bill-report",
    icon: "description",
    color: "bg-primary-600",
    shadow: "shadow-primary-200",
  },

  {
    title: "Invoices",
    path: "/selling-master",
    icon: "receipt_long",
    color: "bg-orange-500",
    shadow: "shadow-orange-200",
  },
  {
    title: "Stock Report",
    path: "/reports/stock-report",
    icon: "analytics",
    color: "bg-indigo-600",
    shadow: "shadow-indigo-200",
  },
  {
    title: "Expenses",
    path: "/expense-category",
    icon: "money_off",
    color: "bg-pink-500",
    shadow: "shadow-pink-200",
  },
  {
    title: "Users",
    path: "/users",
    icon: "manage_accounts",
    color: "bg-slate-600",
    shadow: "shadow-slate-200",
  },
  {
    title: "Currency",
    path: "/currency-uom",
    icon: "settings",
    color: "bg-teal-600",
    shadow: "shadow-teal-200",
  },
  {
    title: "Shipping",
    path: "/shipping-line",
    icon: "directions_boat",
    color: "bg-blue-600",
    shadow: "shadow-blue-200",
  },
  {
    title: "Stock Cat.",
    path: "/stock-category",
    icon: "inventory_2",
    color: "bg-orange-600",
    shadow: "shadow-orange-200",
  },
  {
    title: "Company",
    path: "/company",
    icon: "business",
    color: "bg-indigo-500",
    shadow: "shadow-indigo-200",
  },
  {
    title: "Countries",
    path: "/country",
    icon: "public",
    color: "bg-emerald-600",
    shadow: "shadow-emerald-200",
  },
  {
    title: "Roles",
    path: "/roles",
    icon: "security",
    color: "bg-rose-600",
    shadow: "shadow-rose-200",
  },
];

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

const BL_STAGES = [
  "SI Pending",
  "Loading",
  "In Transit",
  "Arrived",
  "Arrived ICD",
];

const formatCurrency = (value) =>
  `₹${new Intl.NumberFormat("en-IN").format(Number(value) || 0)}`;

const formatPaymentDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    month: 6,
    year: 2026,
  });
  const [data, setData] = useState({
    kpiCards: {},
    financialPerformance: [],
    purchasePaymentMaster: [],
    salesPaymentMaster: [],
    recentShipments: [],
    inventoryByMetal: [],
    yardUtilization: [],
  });
  const [error, setError] = useState(null);
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const yearDropdownRef = useRef(null);

  const [selectedBLForActivity, setSelectedBLForActivity] = useState(null);
  const [loadingBLNo, setLoadingBLNo] = useState(null);

  const handleStatusClick = async (blNo) => {
    if (!blNo) return;
    setLoadingBLNo(blNo);
    try {
      const blDetail = await blService.getBLByNumber(blNo);
      setSelectedBLForActivity(blDetail);
    } catch (err) {
      console.error("Failed to fetch BL details:", err);
      // Fallback
      const matchingShipment = data.recentShipments.find(
        (s) => s.blNo === blNo,
      );
      setSelectedBLForActivity({
        blNumber: blNo,
        status: matchingShipment?.status || "SI Pending",
        eta: matchingShipment?.eta,
      });
    } finally {
      setLoadingBLNo(null);
    }
  };

  const selectedStatus = selectedBLForActivity?.status || "SI Pending";
  const selectedStageIndex = Math.max(BL_STAGES.indexOf(selectedStatus), 0);
  const progressPercent = Math.round(
    ((selectedStageIndex + 1) / BL_STAGES.length) * 100,
  );
  const lastUpdated =
    selectedBLForActivity?.modifiedDate || selectedBLForActivity?.createdDate;

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        yearDropdownRef.current &&
        !yearDropdownRef.current.contains(event.target)
      ) {
        setIsYearDropdownOpen(false);
      }
    }
    if (isYearDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isYearDropdownOpen]);

  const fetchDashboardData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const summary = await dashboardService.getSummary(
        filters.month,
        filters.year,
      );
      setData({
        kpiCards: summary?.kpiCards || {},
        financialPerformance: Array.isArray(summary?.financialPerformance)
          ? summary.financialPerformance
          : [],
        purchasePaymentMaster: Array.isArray(summary?.purchasePaymentMaster)
          ? summary.purchasePaymentMaster
          : [],
        salesPaymentMaster: Array.isArray(summary?.salesPaymentMaster)
          ? summary.salesPaymentMaster
          : [],
        recentShipments: Array.isArray(summary?.recentShipments)
          ? summary.recentShipments
          : [],
        inventoryByMetal: Array.isArray(summary?.inventoryByMetal)
          ? summary.inventoryByMetal
          : [],
        yardUtilization: Array.isArray(summary?.yardUtilization)
          ? summary.yardUtilization
          : [],
      });
    } catch (err) {
      setError(err?.message || "Failed to fetch dashboard data");
    } finally {
      setIsLoading(false);
    }
  }, [filters.month, filters.year]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const kpis = [
    {
      title: "Monthly Financials",
      isLotCard: true,
      icon: "payments",
      color: "text-primary-600",
      bgColor: "bg-primary-50",
      trend: data.kpiCards?.totalSales?.growth || "0%",
      trendUp: !String(data.kpiCards?.totalSales?.growth || "").startsWith("-"),
      stats: [
        {
          label: "Sales",
          value: `₹${new Intl.NumberFormat("en-IN").format(data.kpiCards?.totalSales?.value || 0)}`,
          color: "text-emerald-600",
          size: "text-xs sm:text-[13px] md:text-sm",
        },
        {
          label: "Purchases",
          value: `₹${new Intl.NumberFormat("en-IN").format(data.kpiCards?.totalPurchase?.value || 0)}`,
          color: "text-slate-600 dark:text-slate-300",
          size: "text-xs sm:text-[13px] md:text-sm",
        },
        {
          label: "Expenses",
          value: `₹${new Intl.NumberFormat("en-IN").format(data.kpiCards?.totalExpenses?.value || 0)}`,
          color: "text-rose-500",
          size: "text-xs sm:text-[13px] md:text-sm",
        },
        {
          label: "Overhead",
          value: `₹${new Intl.NumberFormat("en-IN").format(data.kpiCards?.totalOverhead?.value || 0)}`,
          color: "text-amber-500",
          size: "text-xs sm:text-[13px] md:text-sm",
        },
      ],
    },
    {
      title: "Stock Processing in Lots",
      isLotCard: true,
      icon: "layers",
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
      trend: data.kpiCards?.totalLots?.trend || "Stable",
      trendUp: true,
      stats: [
        {
          label: "Total LOTs",
          value: data.kpiCards?.totalLots?.value,
          color: "text-brand-text-primary dark:text-white",
        },
        {
          label: "InProgress",
          value: data.kpiCards?.unlockedLots?.value,
          color: "text-emerald-600",
        },
        {
          label: "Completed",
          value: data.kpiCards?.lockedLots?.value,
          color: "text-rose-500",
        },
      ],
    },
    {
      title: "Pending Shipments",
      isLotCard: true,
      icon: "local_shipping",
      color: "text-amber-600",
      bgColor: "bg-amber-50",
      stats: [
        {
          label: "SI",
          value: data.kpiCards?.siPending?.value || 0,
          color: "text-amber-600",
        },
        {
          label: "Load",
          value: data.kpiCards?.loading?.value || 0,
          color: "text-blue-600",
        },
        {
          label: "Transit",
          value: data.kpiCards?.inTransit?.value || 0,
          color: "text-primary-600",
        },
        {
          label: "Arr",
          value: data.kpiCards?.arrived?.value || 0,
          color: "text-emerald-600",
        },
        {
          label: "ICD",
          value: data.kpiCards?.arrivedICD?.value || 0,
          color: "text-purple-600",
        },
      ],
    },
    // {
    //   title: "Stock Alerts",
    //   value: String(data.kpiCards?.lowStockAlerts?.value ),
    //   icon: "warning_amber",
    //   color: "text-rose-600",
    //   bgColor: "bg-rose-50",
    //   trend: data.kpiCards?.lowStockAlerts?.status || "Good",
    //   trendUp: (data.kpiCards?.lowStockAlerts?.value ) === 0,
    // },
  ];

  if (isLoading) {
    return (
      <div className="flex h-[70vh] w-full items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-100 border-t-primary-600"></div>
          <p className="text-xs font-black uppercase tracking-widest text-slate-400 animate-pulse">
            Syncing Business Intelligence...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-in fade-in duration-500 pb-6">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
        <div>
          <p className="text-[9px] font-black uppercase tracking-[0.25em] text-primary-600">
            {API_CONFIG.isDemo ? "ANAXIS TECH" : "FERROVANT GLOBAL"}
          </p>
          <h1 className="text-xl font-black text-brand-text-primary dark:text-white tracking-tight">
            Executive Dashboard
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="h-6 w-[1px] bg-slate-200 dark:bg-slate-800 mx-1 hidden sm:block"></div>
          <button
            onClick={fetchDashboardData}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 hover:bg-white dark:hover:bg-slate-700 transition-colors"
          >
            <i
              className={`material-icons text-sm text-slate-400 ${isLoading ? "animate-spin" : ""}`}
            >
              refresh
            </i>
            <span className="text-[10px] font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
              Sync Now
            </span>
          </button>
          <div className="h-8 w-[1px] bg-slate-200 dark:bg-slate-800 mx-1"></div>
          <p className="text-xs text-brand-text-secondary dark:text-slate-400">
            Welcome,{" "}
            <span className="font-bold text-slate-900 dark:text-slate-100">
              {user?.name || user?.fullName || "Admin"}
            </span>
          </p>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-rose-50 border border-rose-100 rounded-xl flex items-center gap-3 text-rose-600">
          <i className="material-icons text-lg">error_outline</i>
          <p className="text-[11px] font-bold uppercase tracking-wider">
            {error}
          </p>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {kpis.map((card) => (
          <div
            key={card.title}
            className="enterprise-card enterprise-card-hover p-4 relative group"
          >
            <div className="flex items-start justify-between relative z-10">
              <div className="flex items-center gap-3">
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${card.bgColor} ${card.color} dark:bg-slate-800/50 shadow-sm`}
                >
                  <i className="material-icons text-xl">{card.icon}</i>
                </div>
                <div>
                  <p className="text-[9px] font-black uppercase tracking-widest text-brand-text-secondary dark:text-slate-400">
                    {card.title}
                  </p>
                </div>
              </div>
              {card.trend && (
                <div
                  className={`flex items-center gap-1 px-1.5 py-0.5 rounded-lg text-[10px] font-black ${
                    card.trendUp
                      ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10"
                      : "bg-rose-50 text-rose-600 dark:bg-rose-500/10"
                  }`}
                >
                  {/* <i className="material-icons text-[12px]">
                    {card.trendUp ? "trending_up" : "trending_down"}
                  </i>
                  {card.trend} */}
                </div>
              )}
            </div>
            <div className="mt-3">
              {card.isLotCard ? (
                <div className="flex items-center justify-between mt-2 py-1">
                  {card.stats.map((s, idx) => (
                    <div key={s.label} className="flex items-center flex-1">
                      <div className="flex flex-col items-center flex-1">
                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter mb-1">
                          {s.label}
                        </span>
                        <span
                          className={`font-black leading-none tracking-tight ${s.size || "text-base"} ${s.color}`}
                        >
                          {s.value}
                        </span>
                      </div>
                      {idx < card.stats.length - 1 && (
                        <div className="h-6 w-[1px] bg-slate-100 dark:bg-slate-800/50 mx-1"></div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <p className="text-xl font-black text-brand-text-primary dark:text-white leading-none mt-1">
                    {card.value}
                  </p>
                  {card.breakdown && (
                    <div className="flex gap-3 mt-2.5 pt-2 border-t border-slate-100 dark:border-slate-800/50">
                      {card.breakdown.map((b) => (
                        <div key={b.label} className="flex flex-col">
                          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter mb-0.5">
                            {b.label}
                          </span>
                          <span
                            className={`text-[10px] font-black leading-none ${b.color}`}
                          >
                            {b.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Quick Access Section */}
      {/* <div className="enterprise-card p-4">
        <div className="mb-4 flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400 shadow-sm">
            <i className="material-icons text-lg">bolt</i>
          </div>
          <h2 className="text-xs font-black text-brand-text-primary dark:text-slate-100 tracking-tight uppercase">Quick Actions</h2>
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-9 gap-3">
          {QUICK_ACCESS.map((action) => (
            <button
              key={action.title}
              onClick={() => navigate(action.path)}
              className="group flex flex-col items-center gap-2.5 p-3 rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 hover:bg-white dark:hover:bg-slate-800 hover:shadow-xl hover:shadow-slate-200/50 dark:hover:shadow-none transition-all duration-300 transform hover:-translate-y-1"
            >
              <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${action.color} text-white shadow-lg ${action.shadow} group-hover:scale-110 transition-transform duration-300`}>
                <i className="material-icons text-2xl">{action.icon}</i>
              </div>
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-600 dark:text-slate-400 group-hover:text-primary-600 transition-colors">
                {action.title}
              </span>
            </button>
          ))}
        </div>
      </div> */}

      <div className=" gap-4">
        {/* Financial Performance Trend */}
        <div className="lg:col-span-2 enterprise-card p-4">
          <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400 shadow-sm">
                <i className="material-icons text-lg">insights</i>
              </div>
              <h2 className="text-xs font-black text-brand-text-primary dark:text-slate-100 tracking-tight uppercase">
                Financial Performance
              </h2>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <div className="h-2 w-2 rounded-full bg-primary-600"></div>
                  <span className="text-[9px] font-black uppercase text-slate-400">
                    Sales
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="h-2 w-2 rounded-full bg-slate-400 dark:bg-slate-600"></div>
                  <span className="text-[9px] font-black uppercase text-slate-400">
                    Purchases
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="h-2 w-2 rounded-full bg-rose-500"></div>
                  <span className="text-[9px] font-black uppercase text-slate-400">
                    Expenses
                  </span>
                </div>
                <div
                  className="flex items-center gap-1 relative"
                  ref={yearDropdownRef}
                >
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                    Year:
                  </span>
                  <button
                    onClick={() => setIsYearDropdownOpen(!isYearDropdownOpen)}
                    type="button"
                    className="flex items-center gap-1 text-xs font-bold text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-2.5 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500 transition-all hover:bg-slate-100 dark:hover:bg-slate-700/80"
                  >
                    <span>{filters.year}</span>
                    <svg
                      className={`h-3 w-3 text-slate-400 transition-transform duration-200 ${isYearDropdownOpen ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {isYearDropdownOpen && (
                    <div className="absolute right-0 top-full mt-1.5 w-24 rounded-xl border border-slate-200 bg-white shadow-enterprise-hover dark:border-slate-800 dark:bg-slate-900 z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200 py-1">
                      {YEARS.map((y) => (
                        <button
                          key={y}
                          type="button"
                          onClick={() => {
                            setFilters((prev) => ({
                              ...prev,
                              year: y,
                            }));
                            setIsYearDropdownOpen(false);
                          }}
                          className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-xs font-semibold transition-all ${
                            filters.year === y
                              ? "bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
                              : "text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800"
                          }`}
                        >
                          <span>{y}</span>
                          {filters.year === y && (
                            <svg
                              className="h-3 w-3 text-primary-600 dark:text-primary-400"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="h-[200px] flex items-end justify-between gap-4 px-2">
            {data.financialPerformance.length > 0 ? (
              (() => {
                const maxVal = Math.max(
                  ...data.financialPerformance.map((p) =>
                    Math.max(p.sales || 0, p.purchases || 0, p.expenses || 0),
                  ),
                  1,
                );
                return data.financialPerformance.map((item) => {
                  const salesHeight = Math.max(
                    ((item.sales || 0) / (maxVal * 1.1)) * 100,
                    2,
                  );
                  const purchaseHeight = Math.max(
                    ((item.purchases || 0) / (maxVal * 1.1)) * 100,
                    2,
                  );
                  const expenseHeight = Math.max(
                    ((item.expenses || 0) / (maxVal * 1.1)) * 100,
                    2,
                  );

                  return (
                    <div
                      key={`${item.month}-${item.year}`}
                      className="flex-1 flex flex-col items-center gap-3 group h-full justify-end"
                    >
                      <div className="w-full flex items-end justify-center gap-1 h-full max-h-[160px]">
                        <div
                          className="w-1/3 bg-primary-600 rounded-t-sm transition-all duration-700 group-hover:bg-primary-700 shadow-sm"
                          style={{ height: `${salesHeight}%` }}
                          title={`Sales: ₹${(item.sales || 0).toLocaleString()}`}
                        ></div>
                        <div
                          className="w-1/3 bg-slate-300 dark:bg-slate-700 rounded-t-sm transition-all duration-700 group-hover:bg-slate-400 dark:group-hover:bg-slate-600 shadow-sm"
                          style={{ height: `${purchaseHeight}%` }}
                          title={`Purchases: ₹${(item.purchases || 0).toLocaleString()}`}
                        ></div>
                        <div
                          className="w-1/3 bg-rose-400 dark:bg-rose-600 rounded-t-sm transition-all duration-700 group-hover:bg-rose-50 dark:group-hover:bg-rose-500 shadow-sm"
                          style={{ height: `${expenseHeight}%` }}
                          title={`Expenses: ₹${(item.expenses || 0).toLocaleString()}`}
                        ></div>
                      </div>
                      <span className="text-[9px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                        {item.month}
                      </span>
                    </div>
                  );
                });
              })()
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <p className="text-[9px] font-bold text-slate-400 uppercase">
                  Insufficient financial data to plot trend
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent Shipments */}
        <div className="lg:col-span-1 enterprise-card p-4">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400 shadow-sm">
                <i className="material-icons text-lg">local_shipping</i>
              </div>
              <h2 className="text-xs font-black text-brand-text-primary dark:text-slate-100 tracking-tight uppercase">
                Recent Shipments
              </h2>
            </div>
            <button
              onClick={() => navigate("/bl-master")}
              className="text-[9px] font-black text-primary-600 uppercase tracking-widest hover:underline transition-all"
            >
              View All
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-800/50">
                  <th className="pb-2 text-[9px] font-black uppercase tracking-widest text-slate-400">
                    BL No
                  </th>
                  <th className="pb-2 text-[9px] font-black uppercase tracking-widest text-slate-400">
                    Container No
                  </th>
                  <th className="pb-2 text-[9px] font-black uppercase tracking-widest text-slate-400">
                    Status
                  </th>
                  <th className="pb-2 text-[9px] font-black uppercase tracking-widest text-slate-400 text-right">
                    ETA
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50 dark:divide-slate-800/30">
                {data.recentShipments.length > 0 ? (
                  data.recentShipments.map((shp) => (
                    <tr
                      key={shp.blNo}
                      className="group hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors"
                    >
                      <td className="py-2 text-[11px] font-black text-brand-text-primary dark:text-slate-200">
                        {shp.blNo}
                      </td>
                      <td className="py-2 text-[11px] font-bold text-brand-text-secondary dark:text-slate-400">
                        {shp.containerNo || "N/A"}
                      </td>
                      <td className="py-2">
                        <button
                          onClick={() => handleStatusClick(shp.blNo)}
                          disabled={loadingBLNo !== null}
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[8px] font-black uppercase tracking-wider transition-all hover:scale-105 active:scale-95 focus:outline-none
                        ${
                          shp.status === "Arrived"
                            ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-300"
                            : shp.status === "Custom Hold"
                              ? "bg-amber-50 text-amber-600 dark:bg-amber-500/20 dark:text-amber-300"
                              : "bg-primary-50 text-primary-600 dark:bg-primary-500/20 dark:text-primary-300"
                        } ${loadingBLNo === shp.blNo ? "opacity-75 cursor-wait" : ""}`}
                          title="Click to view shipment status"
                        >
                          {loadingBLNo === shp.blNo ? (
                            <svg
                              className="animate-spin -ml-0.5 mr-1 h-2.5 w-2.5 text-current"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                              />
                            </svg>
                          ) : null}
                          {shp.status}
                        </button>
                      </td>
                      <td className="py-2 text-[11px] font-bold text-brand-text-secondary dark:text-slate-400 text-right">
                        {shp.eta}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="4"
                      className="py-8 text-center text-[10px] font-bold text-slate-400 uppercase"
                    >
                      No recent shipments tracked
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {/* Sales Payment Master */}
        <div className="enterprise-card p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400 shadow-sm">
              <i className="material-icons text-lg">receipt_long</i>
            </div>
            <h2 className="text-xs font-black text-brand-text-primary dark:text-slate-100 tracking-tight uppercase">
              Sales Payments
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-800/50">
                  <th className="pb-2 text-[9px] font-black uppercase tracking-widest text-slate-400">
                    Invoice
                  </th>
                  <th className="pb-2 text-[9px] font-black uppercase tracking-widest text-slate-400">
                    Customer
                  </th>
                  <th className="pb-2 text-[9px] font-black uppercase tracking-widest text-slate-400">
                    Date
                  </th>
                  <th className="pb-2 text-[9px] font-black uppercase tracking-widest text-slate-400 text-right">
                    Amount
                  </th>
                  <th className="pb-2 text-[9px] font-black uppercase tracking-widest text-slate-400 text-right">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50 dark:divide-slate-800/30">
                {data.salesPaymentMaster.length > 0 ? (
                  data.salesPaymentMaster.map((payment) => (
                    <tr
                      key={payment.id}
                      className="group hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors"
                    >
                      <td className="py-2 text-[11px] font-black text-brand-text-primary dark:text-slate-200">
                        {payment.invoiceNo || "N/A"}
                      </td>
                      <td className="py-2 text-[11px] font-bold text-brand-text-secondary dark:text-slate-400">
                        {payment.customerName || "N/A"}
                      </td>
                      <td className="py-2 text-[11px] font-bold text-brand-text-secondary dark:text-slate-400">
                        {formatPaymentDate(payment.date)}
                      </td>
                      <td className="py-2 text-[11px] font-black text-emerald-600 text-right">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="py-2 text-right">
                        {(() => {
                          const remaining = Number(payment.totalAmount || 0) - Number(payment.receivedAmount || 0);
                          const received = Number(payment.receivedAmount || 0);

                          let badgeClass = "bg-rose-50 text-rose-600 dark:bg-rose-500/20 dark:text-rose-300";
                          let statusText = "Pending";

                          if (remaining <= 0) {
                            badgeClass = "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-300";
                            statusText = "Received";
                          } else if (received > 0) {
                            badgeClass = "bg-amber-50 text-amber-600 dark:bg-amber-500/20 dark:text-amber-300";
                            statusText = "Partially Received";
                          }

                          return (
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-[8px] font-black uppercase tracking-wider ${badgeClass}`}
                            >
                              {statusText}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="5"
                      className="py-8 text-center text-[10px] font-bold text-slate-400 uppercase"
                    >
                      No sales payments found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {/* Purchase Payment Master */}
        <div className="enterprise-card p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-50 text-rose-600 dark:bg-rose-900/20 dark:text-rose-400 shadow-sm">
              <i className="material-icons text-lg">payments</i>
            </div>
            <h2 className="text-xs font-black text-brand-text-primary dark:text-slate-100 tracking-tight uppercase">
              Purchase Payments
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-800/50">
                  <th className="pb-2 text-[9px] font-black uppercase tracking-widest text-slate-400">
                    Bill
                  </th>
                  <th className="pb-2 text-[9px] font-black uppercase tracking-widest text-slate-400">
                    Vendor
                  </th>
                  <th className="pb-2 text-[9px] font-black uppercase tracking-widest text-slate-400">
                    Date
                  </th>
                  <th className="pb-2 text-[9px] font-black uppercase tracking-widest text-slate-400 text-right">
                    Amount
                  </th>
                  <th className="pb-2 text-[9px] font-black uppercase tracking-widest text-slate-400 text-right">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50 dark:divide-slate-800/30">
                {data.purchasePaymentMaster.length > 0 ? (
                  data.purchasePaymentMaster.map((payment) => (
                    <tr
                      key={payment.id}
                      className="group hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors"
                    >
                      <td className="py-2 text-[11px] font-black text-brand-text-primary dark:text-slate-200">
                        {payment.billNo || "N/A"}
                      </td>
                      <td className="py-2 text-[11px] font-bold text-brand-text-secondary dark:text-slate-400">
                        {payment.vendorName || "N/A"}
                      </td>
                      <td className="py-2 text-[11px] font-bold text-brand-text-secondary dark:text-slate-400">
                        {formatPaymentDate(payment.date)}
                      </td>
                      <td className="py-2 text-[11px] font-black text-rose-600 text-right">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="py-2 text-right">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-[8px] font-black uppercase tracking-wider ${
                            payment.isPaid
                              ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-300"
                              : "bg-rose-50 text-rose-600 dark:bg-rose-500/20 dark:text-rose-300"
                          }`}
                        >
                          {payment.isPaid ? "Paid" : "Unpaid"}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="5"
                      className="py-8 text-center text-[10px] font-bold text-slate-400 uppercase"
                    >
                      No purchase payments found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {/* Stock Category Distribution */}
        {/* <div className="enterprise-card p-4">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-50 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400 shadow-sm">
              <i className="material-icons text-lg">category</i>
            </div>
            <h2 className="text-xs font-black text-brand-text-primary dark:text-slate-100 tracking-tight uppercase">Inventory by Metal</h2>
          </div>
          
          <div className="space-y-4">
            {data.inventoryByMetal.length > 0 ? data.inventoryByMetal.map((item, idx) => (
              <div key={item.name}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] font-black text-brand-text-secondary dark:text-slate-300 uppercase truncate pr-2">{item.name}</span>
                  <span className="text-[10px] font-black text-brand-text-primary dark:text-white">{new Intl.NumberFormat('en-IN').format(item.weight)} <span className="text-[8px] text-slate-400">KG</span></span>
                </div>
                <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all duration-1000 ease-out ${
                      ['bg-orange-500', 'bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500', 'bg-indigo-500'][idx % 6]
                    }`}
                    style={{ width: `${item.percent}%` }}
                  ></div>
                </div>
              </div>
            )) : (
               <div className="py-12 text-center">
                 <p className="text-[10px] font-bold text-slate-400 uppercase">Warehouse is empty</p>
               </div>
            )}
          </div>
        </div> */}
        {/* Yard Utilization */}
        {/* <div className="enterprise-card p-4">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400 shadow-sm">
              <i className="material-icons text-lg">warehouse</i>
            </div>
            <h2 className="text-xs font-black text-brand-text-primary dark:text-slate-100 tracking-tight uppercase">Yard Utilization</h2>
          </div>
          
          <div className="space-y-5 py-2">
            {data.yardUtilization.length > 0 ? data.yardUtilization.map((yard) => (
              <div key={yard.name} className="relative">
                <div className="flex justify-between items-center mb-1">
                  <p className="text-[10px] font-black text-brand-text-secondary dark:text-slate-300 uppercase">{yard.name}</p>
                  <span className={`text-[8px] font-black uppercase px-1.5 py-0.5 rounded ${
                    yard.status === 'High' ? 'bg-rose-50 text-rose-600' : yard.status === 'Optimal' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
                  }`}>{yard.status}</span>
                </div>
                <div className="h-4 w-full bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700 overflow-hidden p-0.5">
                  <div 
                    className={`h-full rounded-md transition-all duration-1000 flex items-center justify-end px-2 ${
                      yard.status === 'High' ? 'bg-rose-500' : yard.status === 'Optimal' ? 'bg-emerald-500' : 'bg-amber-500'
                    }`}
                    style={{ width: yard.capacity }}
                  >
                    <span className="text-[8px] font-black text-white">{yard.capacity}</span>
                  </div>
                </div>
              </div>
            )) : (
              <div className="py-8 text-center text-[10px] font-bold text-slate-400 uppercase">No active yards found</div>
            )}
          </div>
        </div> */}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4"></div>

      <Modal
        isOpen={Boolean(selectedBLForActivity)}
        onClose={() => setSelectedBLForActivity(null)}
        title="BL Shipment Status"
      >
        <div className="space-y-3">
          <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 dark:border-slate-700 dark:bg-slate-900/40">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] text-slate-500 uppercase font-bold">
                  BL Number
                </p>
                <p className="text-sm font-black">
                  {selectedBLForActivity?.blNumber}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-slate-500 uppercase font-bold">
                  Current Stage
                </p>
                <p className="text-sm font-black text-emerald-600 dark:text-emerald-400">
                  {selectedStatus}
                </p>
              </div>
            </div>
          </div>

          <div className="px-1">
            <div className="flex justify-between items-end mb-2">
              <span className="text-xs font-black text-slate-800 dark:text-white uppercase tracking-tight">
                Shipping Progress
              </span>
              <span className="text-xs font-black text-emerald-600 dark:text-emerald-400">
                {progressPercent}%
              </span>
            </div>
            <div className="h-2 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          <div className="space-y-2 max-h-[50vh] pr-1 overflow-auto">
            {BL_STAGES.map((stage, index) => {
              const isDone = index < selectedStageIndex;
              const isActive = index === selectedStageIndex;
              const isDoneOrActive = index <= selectedStageIndex;
              return (
                <div
                  key={stage}
                  className={`flex gap-3 p-3 rounded-xl border transition-all ${isActive ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800" : "bg-white border-slate-100 dark:bg-slate-900 dark:border-slate-800"}`}
                >
                  <div className="flex flex-col items-center">
                    <div
                      className={`h-6 w-6 rounded-full flex items-center justify-center border-2 ${isDoneOrActive ? "bg-emerald-500 border-emerald-500" : "border-slate-200"}`}
                    >
                      {isDoneOrActive ? (
                        <svg
                          className="h-4 w-4 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      ) : (
                        <div
                          className="h-1.5 w-1.5 rounded-full bg-slate-200"
                        />
                      )}
                    </div>
                    {index < BL_STAGES.length - 1 && (
                      <div
                        className={`w-0.5 h-full min-h-[10px] mt-1 ${isDone ? "bg-emerald-500" : "bg-slate-100 dark:bg-slate-800"}`}
                      />
                    )}
                  </div>
                  <div className="flex-1">
                    <p
                      className={`text-xs font-black uppercase tracking-tight ${isActive ? "text-emerald-700 dark:text-emerald-400" : isDone ? "text-emerald-700 dark:text-emerald-400" : "text-slate-400"}`}
                    >
                      {stage}
                    </p>
                    {isActive && (
                      <p className="text-[10px] text-emerald-600/70 font-semibold italic mt-0.5">
                        Current Status
                      </p>
                    )}
                    {(isDone || isActive) && (
                      <p className="text-[9px] text-slate-500 mt-1">
                        Updated {formatDateTime(lastUpdated)}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default DashboardPage;
